// Shared Situation Map types and district visualization data.

export type Region = 'CCR' | 'RCR' | 'OCR';
export type ProjectType = 'new-launch' | 'gls';

export interface MapProject {
  id: string;
  name: string;
  developer: string;
  lat: number;
  lng: number;
  district: string; // e.g. "D09"
  region: Region;
  topYear: number;
  totalUnits: number;
  soldUnits: number;
  type: ProjectType;
  upcoming?: boolean;
  // Simple polygon plot (lat/lng pairs) for "plot view"
  plot: [number, number][];
}

export interface DistrictInfo {
  code: string; // "D09"
  name: string;
  region: Region;
  // approximate centroid for polygon-less rendering
  center: [number, number];
  // crude polygon — rectangular approximation around center
  polygon: [number, number][];
  metrics: {
    transactions: number;
    listings: number;
    eoi: number;
    resaleSales: number;
    resaleRental: number;
  };
}

// Helper to build a crude rectangular polygon around a center
function rect(
  lat: number,
  lng: number,
  dLat: number,
  dLng: number,
): [number, number][] {
  return [
    [lat - dLat, lng - dLng],
    [lat - dLat, lng + dLng],
    [lat + dLat, lng + dLng],
    [lat + dLat, lng - dLng],
  ];
}

// Metrics are intentionally NOT correlated across columns:
//   • transactions tend to be highest in OCR (mass-market HDB upgrader districts)
//   • listings concentrate in CCR (high inventory of premium homes for resale/rental)
//   • EOI heat is driven by where current new launches are hyped
export const DISTRICTS: DistrictInfo[] = [
  {
    code: 'D01',
    name: 'Raffles Place / Marina',
    region: 'CCR',
    center: [1.2841, 103.8513],
    polygon: rect(1.2841, 103.8513, 0.012, 0.012),
    metrics: { transactions: 58, listings: 245, eoi: 32, resaleSales: 21, resaleRental: 64 },
  },
  {
    code: 'D02',
    name: 'Tanjong Pagar / Chinatown',
    region: 'CCR',
    center: [1.2769, 103.8438],
    polygon: rect(1.2769, 103.8438, 0.010, 0.010),
    metrics: { transactions: 72, listings: 312, eoi: 48, resaleSales: 31, resaleRental: 88 },
  },
  {
    code: 'D03',
    name: 'Tiong Bahru / Queenstown',
    region: 'RCR',
    center: [1.2900, 103.8200],
    polygon: rect(1.2900, 103.8200, 0.012, 0.013),
    metrics: { transactions: 142, listings: 188, eoi: 95, resaleSales: 62, resaleRental: 120 },
  },
  {
    code: 'D05',
    name: 'Buona Vista / Pasir Panjang',
    region: 'RCR',
    center: [1.2920, 103.7800],
    polygon: rect(1.2920, 103.7800, 0.015, 0.020),
    metrics: { transactions: 168, listings: 220, eoi: 78, resaleSales: 78, resaleRental: 140 },
  },
  {
    code: 'D09',
    name: 'Orchard / River Valley',
    region: 'CCR',
    center: [1.3000, 103.8400],
    polygon: rect(1.3000, 103.8400, 0.011, 0.012),
    metrics: { transactions: 118, listings: 396, eoi: 142, resaleSales: 55, resaleRental: 168 },
  },
  {
    code: 'D10',
    name: 'Bukit Timah / Holland',
    region: 'CCR',
    center: [1.3200, 103.8050],
    polygon: rect(1.3200, 103.8050, 0.015, 0.020),
    metrics: { transactions: 92, listings: 348, eoi: 88, resaleSales: 48, resaleRental: 115 },
  },
  {
    code: 'D11',
    name: 'Newton / Novena',
    region: 'CCR',
    center: [1.3220, 103.8430],
    polygon: rect(1.3220, 103.8430, 0.011, 0.013),
    metrics: { transactions: 88, listings: 305, eoi: 62, resaleSales: 37, resaleRental: 96 },
  },
  {
    code: 'D14',
    name: 'Geylang / Eunos',
    region: 'RCR',
    center: [1.3180, 103.8870],
    polygon: rect(1.3180, 103.8870, 0.012, 0.016),
    metrics: { transactions: 178, listings: 162, eoi: 248, resaleSales: 70, resaleRental: 110 },
  },
  {
    code: 'D15',
    name: 'Marine Parade / Katong',
    region: 'RCR',
    center: [1.3030, 103.9050],
    polygon: rect(1.3030, 103.9050, 0.013, 0.018),
    metrics: { transactions: 210, listings: 238, eoi: 295, resaleSales: 90, resaleRental: 160 },
  },
  {
    code: 'D16',
    name: 'Bedok / Upper East Coast',
    region: 'OCR',
    center: [1.3236, 103.9270],
    polygon: rect(1.3236, 103.9270, 0.014, 0.020),
    metrics: { transactions: 312, listings: 245, eoi: 138, resaleSales: 112, resaleRental: 138 },
  },
  {
    code: 'D17',
    name: 'Loyang / Changi',
    region: 'OCR',
    center: [1.3460, 103.9810],
    polygon: rect(1.3460, 103.9810, 0.018, 0.025),
    metrics: { transactions: 88, listings: 72, eoi: 45, resaleSales: 41, resaleRental: 62 },
  },
  {
    code: 'D18',
    name: 'Tampines / Pasir Ris',
    region: 'OCR',
    center: [1.3540, 103.9500],
    polygon: rect(1.3540, 103.9500, 0.016, 0.022),
    metrics: { transactions: 392, listings: 282, eoi: 165, resaleSales: 144, resaleRental: 152 },
  },
  {
    code: 'D19',
    name: 'Hougang / Serangoon',
    region: 'OCR',
    center: [1.3680, 103.8740],
    polygon: rect(1.3680, 103.8740, 0.014, 0.018),
    metrics: { transactions: 268, listings: 198, eoi: 332, resaleSales: 128, resaleRental: 134 },
  },
  {
    code: 'D20',
    name: 'Bishan / Ang Mo Kio',
    region: 'RCR',
    center: [1.3690, 103.8460],
    polygon: rect(1.3690, 103.8460, 0.013, 0.016),
    metrics: { transactions: 235, listings: 182, eoi: 128, resaleSales: 108, resaleRental: 142 },
  },
  {
    code: 'D21',
    name: 'Clementi / Upper Bukit Timah',
    region: 'RCR',
    center: [1.3360, 103.7700],
    polygon: rect(1.3360, 103.7700, 0.015, 0.022),
    metrics: { transactions: 188, listings: 175, eoi: 95, resaleSales: 72, resaleRental: 124 },
  },
  {
    code: 'D22',
    name: 'Jurong / Boon Lay',
    region: 'OCR',
    center: [1.3340, 103.7270],
    polygon: rect(1.3340, 103.7270, 0.020, 0.028),
    metrics: { transactions: 358, listings: 215, eoi: 188, resaleSales: 150, resaleRental: 162 },
  },
  {
    code: 'D23',
    name: 'Hillview / Bukit Panjang',
    region: 'OCR',
    center: [1.3760, 103.7700],
    polygon: rect(1.3760, 103.7700, 0.018, 0.024),
    metrics: { transactions: 215, listings: 158, eoi: 108, resaleSales: 84, resaleRental: 114 },
  },
  {
    code: 'D25',
    name: 'Woodlands / Admiralty',
    region: 'OCR',
    center: [1.4360, 103.7860],
    polygon: rect(1.4360, 103.7860, 0.018, 0.026),
    metrics: { transactions: 305, listings: 155, eoi: 142, resaleSales: 138, resaleRental: 142 },
  },
  {
    code: 'D27',
    name: 'Yishun / Sembawang',
    region: 'OCR',
    center: [1.4280, 103.8350],
    polygon: rect(1.4280, 103.8350, 0.018, 0.024),
    metrics: { transactions: 288, listings: 145, eoi: 152, resaleSales: 130, resaleRental: 144 },
  },
  {
    code: 'D28',
    name: 'Seletar / Yio Chu Kang',
    region: 'OCR',
    center: [1.4040, 103.8700],
    polygon: rect(1.4040, 103.8700, 0.016, 0.022),
    metrics: { transactions: 125, listings: 88, eoi: 68, resaleSales: 62, resaleRental: 88 },
  },
];
